/**
 * Copyright (c) 2023 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useState } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { Form } from 'react-final-form';
import { useDispatch } from 'react-redux';
import { ActionButton, BrandColors, Modal, TextboxInput, Text } from '@blend/enterprise-components';
import { setRoomError } from '../../../../../actions/roomActions';
import { useNotes } from '../../../../../hooks/useNotes';
import { notaryAPI } from '../../../../../api/notary';
import { ModalContent } from '@components/common/enterprise/ModalContent/ModalContent';

const rowSingle = (margin: number) => {
  return {
    marginBottom: margin,
    display: 'flex',
  };
};

const styles = StyleSheet.create({
  footerBar: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingTop: 14,
    borderTop: `1px solid ${BrandColors.CLOUD_50}`,
  },
  rowSingle: rowSingle(24),
  textBoxError: rowSingle(10),
  submitWrapper: {
    marginLeft: 8,
  },
});

export namespace NoteModal {
  export interface Props {
    roomId: string;
    headerText: string;
    subHeaderText?: string;
    handleClose: () => void;
  }
}

export const NoteModal: React.FC<NoteModal.Props> = ({ roomId, headerText, subHeaderText, handleClose }) => {
  const dispatch = useDispatch();
  const [value, setValue] = useState<string>('');
  const [error, setError] = useState<boolean>(false);
  const { dispatchNotes } = useNotes(roomId);

  const onSubmit = async () => {
    try {
      if (value.length === 0) {
        setError(true);
        return;
      }
      setError(false);
      await notaryAPI.rooms.createRoomNote(roomId, { message: value });
      await dispatchNotes();
      handleClose();
    } catch (error) {
      setRoomError({ hasError: true })(dispatch);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const note = event.target.value;
    if (note.length === 0) {
      setError(true);
    } else {
      setError(false);
    }
    setValue(note);
  };

  return (
    <Modal data-it="NoteModal" contentLabel={headerText} handleClose={handleClose} isOpen={true}>
      <ModalContent handleClose={handleClose} headerText={headerText} subHeaderText={subHeaderText}>
        <Form onSubmit={onSubmit}>
          {({ handleSubmit, submitting }) => {
            return (
              <div>
                <div className={error ? css(styles.textBoxError) : css(styles.rowSingle)}>
                  <TextboxInput
                    data-it="NoteModal-textbox"
                    overflow="scroll"
                    placeholder="Enter note..."
                    rows={3}
                    value={value}
                    hasError={error}
                    onChange={handleChange}
                  />
                </div>
                {error && (
                  <div>
                    <Text data-it="NoteModal-error" colorName="ACCENT_RED">
                      You cannot save an empty note.
                    </Text>
                  </div>
                )}
                <div className={css(styles.footerBar)}>
                  <ActionButton
                    data-dd-privacy="allow"
                    data-it="NoteModal-cancel"
                    label="Cancel"
                    onClick={handleClose}
                    theme="secondary"
                  />
                  <div className={css(styles.submitWrapper)}>
                    <ActionButton
                      data-dd-privacy="allow"
                      disabled={submitting || error}
                      data-it="NoteModal-submit"
                      label="Save"
                      onClick={handleSubmit}
                    />
                  </div>
                </div>
              </div>
            );
          }}
        </Form>
      </ModalContent>
    </Modal>
  );
};
